import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface SignDocumentModalProps {
  open: boolean;
  title: string;
  expectedName: string;
  alreadySigned?: boolean;
  children: React.ReactNode;
  onClose: () => void;
  onConfirm: (typedName: string) => Promise<void>;
}

const normalize = (s: string) =>
  s
    // Treat curly/smart quotes as their straight equivalents so a name like
    // O'Brien matches whether the user typed a straight ' or a curly ' (many
    // keyboards/autocorrect insert the curly form). Covers apostrophes and
    // single/double quotes. Keep this in sync with the backend normalize in
    // apps/tenant-api/src/modules/signature/signature.service.ts.
    .replace(/[‘’‛ʼ′`]/g, "'")
    .replace(/[“”″]/g, '"')
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase();

export const SignDocumentModal: React.FC<SignDocumentModalProps> = ({
  open,
  title,
  expectedName,
  alreadySigned = false,
  children,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [timestamp, setTimestamp] = useState<string>(() =>
    new Date().toLocaleString(),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state each time the modal opens. If the document is already signed
  // in this session, prefill name + checkbox so the user just reviews and
  // closes; "Confirm" becomes a no-op close (no second API call).
  useEffect(() => {
    if (open) {
      setName(alreadySigned ? expectedName : "");
      setAgreed(alreadySigned);
      setTimestamp(new Date().toLocaleString());
      setSubmitting(false);
      setError(null);
    }
  }, [open, alreadySigned, expectedName]);

  if (!open) return null;

  const canSubmit = agreed && name.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    setError(null);

    if (alreadySigned) {
      // Already recorded in this session — Confirm just dismisses the modal.
      onClose();
      return;
    }

    if (normalize(name) !== normalize(expectedName)) {
      setError(t("sda.signatures.modal.errorNameMismatch"));
      return;
    }

    setSubmitting(true);
    setTimestamp(new Date().toLocaleString());
    try {
      await onConfirm(name);
    } catch (err: any) {
      console.error("Signature submit error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          t("sda.signatures.modal.errorDefault"),
      );
      setSubmitting(false);
    }
  };

  return (
    // role/aria-modal are what assistive tech expects on a blocking dialog,
    // and the support widget keys off them to hide its launcher — without
    // them it floats above this overlay and covers the action buttons.
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h4 className="font-semibold text-primary">{title}</h4>
          <button
            type="button"
            aria-label="Close"
            className="text-gray-400 transition-colors hover:text-gray-700 cursor-pointer"
            onClick={onClose}
            disabled={submitting}
          >
            ✕
          </button>
        </div>

        <div className="grow overflow-y-auto px-6 py-5">{children}</div>

        <div className="border-t border-gray-100 px-6 py-5 space-y-3">
          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
              {error}
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              {t("sda.signatures.modal.yourName")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500"
              disabled={submitting || alreadySigned}
            />
          </div>

          <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/20"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={submitting || alreadySigned}
            />
            <span>{t("sda.signatures.modal.agree")}</span>
          </label>

          <p className="text-xs text-muted">
            {t("sda.signatures.modal.date")} {timestamp}
          </p>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onClose}
              disabled={submitting}
            >
              {t("sda.signatures.modal.cancel")}
            </button>
            <button
              type="button"
              className="rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-light cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting
                ? t("sda.signatures.modal.submitting")
                : t("sda.signatures.modal.confirm")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignDocumentModal;
